// ──────────────────────────────────────────────────────────────────────
// D60 PHASE 1 — Client Locations tab.
//
// Multi-location franchise support. Each client (e.g. "India Curry House")
// can have N location children (e.g. "Regina — Albert St", "Saskatoon —
// 8th St"). 0 locations = single-site client (no UI change in other tabs).
//
// Inherits from D59 live-reactivity layer: 15s poll + focus refetch + cross-
// tab `stellar-client-locations-changed` broadcast on mutation.
// ──────────────────────────────────────────────────────────────────────
import React, { useState, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { MapPin, Plus, Edit3, Trash2, Phone, Clock, User, Loader2, AlertCircle } from 'lucide-react';
import { clientGroupAPI } from '../lib/api';
import useLiveData, { broadcastChange } from '../lib/useLiveData';
import LastSyncedPill from './LastSyncedPill';
import { toast } from 'sonner';

const EMPTY_LOC = {
  location_name: '', address: '', phone: '', hours: '',
  store_manager_name: '', store_manager_email: '', store_manager_phone: '',
  timezone: 'America/Regina', active: true,
  override_logo_url: '', location_hashtags: [], assigned_team: [], notes: '',
};

export default function ClientLocationsTab({ workspaceId, clientId, clientName }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = new, else the location row
  const [form, setForm] = useState(EMPTY_LOC);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Live list (15s poll + focus + broadcast event).
  const { data: locations, loading, lastSynced, refetch } = useLiveData(
    async () => {
      if (!workspaceId || !clientId) return [];
      const r = await clientGroupAPI.listLocations(workspaceId, clientId);
      return r.data || [];
    },
    [workspaceId, clientId],
    {
      intervalMs: 15_000,
      refetchOnFocus: true,
      events: ['stellar-client-locations-changed'],
      enabled: !!(workspaceId && clientId),
      initialData: [],
      topic: 'clients',
    },
  );

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_LOC);
    setModalOpen(true);
  };

  const openEdit = (loc) => {
    setEditing(loc);
    setForm({
      location_name: loc.location_name || '',
      address: loc.address || '',
      phone: loc.phone || '',
      hours: loc.hours || '',
      store_manager_name: loc.store_manager_name || '',
      store_manager_email: loc.store_manager_email || '',
      store_manager_phone: loc.store_manager_phone || '',
      timezone: loc.timezone || 'America/Regina',
      active: loc.active !== false,
      override_logo_url: loc.override_logo_url || '',
      location_hashtags: loc.location_hashtags || [],
      assigned_team: loc.assigned_team || [],
      notes: loc.notes || '',
    });
    setModalOpen(true);
  };

  const handleSave = useCallback(async () => {
    if (!form.location_name.trim()) {
      toast.error('Location name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await clientGroupAPI.updateLocation(workspaceId, clientId, editing.id, form);
        toast.success('Location updated');
      } else {
        await clientGroupAPI.createLocation(workspaceId, clientId, form);
        toast.success('Location added');
      }
      broadcastChange('stellar-client-locations-changed', { clientId });
      setModalOpen(false);
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [form, editing, workspaceId, clientId, refetch]);

  const handleDelete = useCallback(async (loc) => {
    try {
      await clientGroupAPI.deleteLocation(workspaceId, clientId, loc.id);
      toast.success(`Deleted ${loc.location_name}`);
      broadcastChange('stellar-client-locations-changed', { clientId });
      setConfirmDelete(null);
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  }, [workspaceId, clientId, refetch]);

  return (
    <div className="space-y-4" data-testid="client-locations-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[18px] font-semibold text-foreground">Locations</h2>
          <p className="text-[11px] text-muted-foreground/50 mt-0.5">
            {(locations || []).length === 0
              ? 'No locations yet — add one if this client operates from multiple sites.'
              : `${(locations || []).length} location${(locations || []).length === 1 ? '' : 's'} under ${clientName || 'this client'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <LastSyncedPill lastSynced={lastSynced} loading={loading} onRefetch={refetch} />
          <Button size="sm" onClick={openNew} className="h-8 text-[12px] bg-gold/15 hover:bg-gold/25 text-gold border-0" data-testid="add-location-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Location
          </Button>
        </div>
      </div>

      {loading && (locations || []).length === 0 ? (
        <div className="text-center py-12 text-[12px] text-muted-foreground/50">
          <Loader2 className="h-4 w-4 animate-spin inline mr-1.5" /> Loading locations…
        </div>
      ) : (locations || []).length === 0 ? (
        <Card className="bg-card border-dashed border-border/30">
          <CardContent className="p-8 text-center" data-testid="locations-empty">
            <MapPin className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
            <div className="text-[12px] text-muted-foreground/50 mb-3">
              No locations yet. Add one for each physical site.
            </div>
            <Button size="sm" variant="outline" onClick={openNew} className="h-8 text-[12px] border-border/30">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add first location
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(locations || []).map(loc => (
            <Card key={loc.id} className={`bg-card border-border/30 hover:border-gold/30 transition-colors ${!loc.active ? 'opacity-60' : ''}`} data-testid={`location-card-${loc.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-gold shrink-0" />
                      <div className="text-[13px] font-medium text-foreground truncate" data-testid={`location-name-${loc.id}`}>
                        {loc.location_name}
                      </div>
                      {!loc.active && (
                        <span className="text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-foreground/[0.05] text-muted-foreground/60">Inactive</span>
                      )}
                    </div>
                    {loc.address && (
                      <div className="text-[11px] text-muted-foreground/50 mt-1 truncate" title={loc.address}>{loc.address}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(loc)} className="p-1 rounded hover:bg-foreground/[0.05] text-muted-foreground/40 hover:text-foreground" aria-label="Edit" data-testid={`location-edit-${loc.id}`}>
                      <Edit3 className="h-3 w-3" />
                    </button>
                    <button onClick={() => setConfirmDelete(loc)} className="p-1 rounded hover:bg-red-500/10 text-muted-foreground/40 hover:text-red-400" aria-label="Delete" data-testid={`location-delete-${loc.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <div className="space-y-1 text-[11px] text-muted-foreground/60">
                  {loc.phone && <div className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {loc.phone}</div>}
                  {loc.hours && <div className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {loc.hours}</div>}
                  {loc.store_manager_name && <div className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {loc.store_manager_name}</div>}
                </div>
                {loc.location_hashtags?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {loc.location_hashtags.map(tag => (
                      <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-gold/10 text-gold">#{tag.replace(/^#/, '')}</span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add / Edit modal */}
      <Dialog open={modalOpen} onOpenChange={(o) => { if (!saving) setModalOpen(o); }}>
        <DialogContent className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto" data-testid="location-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-[16px]">
              {editing ? 'Edit Location' : 'Add Location'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <FormField label="Location name *" testid="loc-name">
              <Input value={form.location_name} onChange={e => setForm(f => ({ ...f, location_name: e.target.value }))} placeholder="e.g. Regina — Albert St" data-testid="location-name-input" />
            </FormField>
            <FormField label="Full address" testid="loc-address">
              <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="123 Albert St, Regina, SK S4R 2N6" data-testid="location-address-input" />
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Phone">
                <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(306) 555-1234" data-testid="location-phone-input" />
              </FormField>
              <FormField label="Hours">
                <Input value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} placeholder="Mon-Fri 11am-10pm" data-testid="location-hours-input" />
              </FormField>
            </div>
            <div className="border-t border-border/20 pt-3">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground/50 mb-2">Store Manager</div>
              <div className="grid grid-cols-1 gap-2">
                <Input value={form.store_manager_name} onChange={e => setForm(f => ({ ...f, store_manager_name: e.target.value }))} placeholder="Name" data-testid="location-mgr-name-input" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={form.store_manager_email} onChange={e => setForm(f => ({ ...f, store_manager_email: e.target.value }))} placeholder="email@…" data-testid="location-mgr-email-input" />
                  <Input value={form.store_manager_phone} onChange={e => setForm(f => ({ ...f, store_manager_phone: e.target.value }))} placeholder="Phone" data-testid="location-mgr-phone-input" />
                </div>
              </div>
            </div>
            <div className="border-t border-border/20 pt-3">
              <FormField label="Location hashtags (comma-separated)">
                <Input
                  value={(form.location_hashtags || []).join(', ')}
                  onChange={e => setForm(f => ({ ...f, location_hashtags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                  placeholder="YQR, ReginaEats, IndiaCurryHouseRegina"
                  data-testid="location-hashtags-input"
                />
              </FormField>
              <FormField label="Timezone">
                <Input value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))} data-testid="location-tz-input" />
              </FormField>
              <FormField label="Notes">
                <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Anything else worth knowing…" data-testid="location-notes-input" />
              </FormField>
              <label className="flex items-center gap-2 text-[12px] text-foreground cursor-pointer mt-2" data-testid="location-active-toggle">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={e => setForm(f => ({ ...f, active: e.target.checked }))}
                  className="rounded border-border/40 bg-card text-gold focus:ring-gold/40"
                />
                Active (uncheck to hide from content targeting)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)} disabled={saving} className="text-[12px] h-8 border-border/30" data-testid="location-cancel">Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="text-[12px] h-8 bg-gold/15 hover:bg-gold/25 text-gold border-0" data-testid="location-save">
              {saving ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : null}
              {editing ? 'Save changes' : 'Add Location'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-[400px]" data-testid="location-delete-confirm">
          <DialogHeader>
            <DialogTitle className="font-display text-[16px] flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-400" /> Delete location?
            </DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground/70 py-2">
            <span className="text-foreground font-medium">{confirmDelete?.location_name}</span> will be removed permanently. Any content posts targeting this location will fall back to the parent client.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(null)} className="text-[12px] h-8 border-border/30">Cancel</Button>
            <Button size="sm" onClick={() => handleDelete(confirmDelete)} className="text-[12px] h-8 bg-red-500/15 hover:bg-red-500/25 text-red-300 border-0" data-testid="location-delete-confirm-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ label, children, testid }) {
  return (
    <div className="space-y-1" data-testid={testid}>
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground/60">{label}</Label>
      {children}
    </div>
  );
}
